import React, { useState, useEffect } from 'react';
import {
    Button,
    Card,
    Checkbox,
    Group,
    Pagination,
    Stack,
    Table,
    Tabs,
    Text,
    TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { IconSearch, IconUsers, IconBuilding, IconBuildingHospital } from '@tabler/icons-react';
import { 
    useAvailableHcps, 
    useCreateDelegation, 
    usePatientDelegations 
} from '../../../global/hooks/useHcpDelegation.tsx';
import { AccessLevel, IAvailableHcpModel, ICreateDelegationModel } from '../../../global/apis/HcpDelegationApi/HcpDelegationApi';
import { IListItem } from '../../../global/apis/SexesApi/SexesApi.ts';

interface IHcpDelegationFormProps {
    patientID?: number;
    onSuccess?: () => void;
}

interface IFormValues {
    delegatedHcpIDs: string[];
    accessLevel: string;
    reason: string;
}

const HcpDelegationForm: React.FC<IHcpDelegationFormProps> = ({ 
    patientID, 
    onSuccess 
}) => {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sameFacilitySearch, setSameFacilitySearch] = useState('');
    const [otherFacilitySearch, setOtherFacilitySearch] = useState('');
    const [sameFacilityPage, setSameFacilityPage] = useState(1);
    const [otherFacilityPage, setOtherFacilityPage] = useState(1);

    const ITEMS_PER_PAGE = 10;

    const { data: availableHcps, isLoading: loadingHcps } = useAvailableHcps();
    const { data: patientDelegations } = usePatientDelegations(patientID || 0);
    const createDelegation = useCreateDelegation();

    const form = useForm<IFormValues>({
        initialValues: {
            delegatedHcpIDs: [],
            accessLevel: AccessLevel.ReadOnly.toString(),
            reason: '',
        },
        validate: {
            delegatedHcpIDs: (value) => !value.length ? t('delegation.validation.hcpRequired') : null
        },
    });

    const handleSubmit = async (values: IFormValues) => {
        if (!patientID) {
            console.error('Patient ID is required');
            return;
        }

        setIsSubmitting(true);
        try {
            const request: ICreateDelegationModel = {
                delegatedHcpID: 0, // Keep for backward compatibility
                delegatedHcpIDs: values.delegatedHcpIDs.map(id => parseInt(id)),
                patientID: patientID,
                accessLevel: parseInt(values.accessLevel) as AccessLevel,
                reason: values.reason || undefined,
            };

            await createDelegation.mutateAsync(request);
            form.reset();
            onSuccess?.();
        } catch (error) {
            console.error('Error creating delegation:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Helper function to search/filter HCPs
    const containsStringIgnoreCase = (strings: string[], searchTerm: string): boolean => {
        if (!searchTerm) return true;
        const lowerSearchTerm = searchTerm.toLowerCase();
        return strings.some(str => str && str.toLowerCase().includes(lowerSearchTerm));
    };

    // Helper function to paginate data
    const paginateData = <T,>(data: T[], page: number, itemsPerPage: number): { 
        paginatedData: T[], 
        totalPages: number 
    } => {
        const totalPages = Math.ceil(data.length / itemsPerPage);
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedData = data.slice(startIndex, endIndex);
        
        return { paginatedData, totalPages };
    };

    const filterHcpsBySearch = (hcps: IAvailableHcpModel[], searchTerm: string): IAvailableHcpModel[] => {
        if (!searchTerm) return hcps;
        
        return hcps.filter(hcp => {
            const specialtyName = hcp.healthcareProfessionalTypes.find(
                (type: IListItem) => type.id === hcp.specialty
            )?.name || '';
            
            return containsStringIgnoreCase([
                hcp.name,
                specialtyName,
                hcp.email
            ], searchTerm);
        });
    };

    // Get list of HCP IDs that already have active delegations for this patient
    const alreadyDelegatedHcpIds = new Set(
        patientDelegations?.filter(d => d.isActive).map(d => d.delegatedHcpID) || []
    );

    // Group HCPs by facility membership and filter out already delegated HCPs
    const sameFacilityHcpsUnfiltered = availableHcps?.filter(hcp => 
        hcp.belongsToCurrentFacilities && !alreadyDelegatedHcpIds.has(hcp.id)
    ) || [];
    const otherFacilityHcpsUnfiltered = availableHcps?.filter(hcp => 
        !hcp.belongsToCurrentFacilities && !alreadyDelegatedHcpIds.has(hcp.id)
    ) || [];

    // Apply search filtering to both groups
    const sameFacilityHcps = filterHcpsBySearch(sameFacilityHcpsUnfiltered, sameFacilitySearch);
    const otherFacilityHcps = filterHcpsBySearch(otherFacilityHcpsUnfiltered, otherFacilitySearch);

    // Reset pagination when search results change
    useEffect(() => {
        const sameFacilityPages = Math.ceil(sameFacilityHcps.length / ITEMS_PER_PAGE);
        if (sameFacilityHcps.length > 0 && sameFacilityPage > sameFacilityPages) 
            setSameFacilityPage(1);
        
    }, [sameFacilityHcps.length, sameFacilityPage, ITEMS_PER_PAGE]);

    useEffect(() => {
        const otherFacilityPages = Math.ceil(otherFacilityHcps.length / ITEMS_PER_PAGE);
        if (otherFacilityHcps.length > 0 && otherFacilityPage > otherFacilityPages) 
            setOtherFacilityPage(1);
        
    }, [otherFacilityHcps.length, otherFacilityPage, ITEMS_PER_PAGE]);

    const selectedHcpIds = form.values.delegatedHcpIDs;

    const handleHcpSelection = (hcpId: string, checked: boolean) => {
        const currentIds = form.values.delegatedHcpIDs;
        if (checked) 
            form.setFieldValue('delegatedHcpIDs', [...currentIds, hcpId]);
         else 
            form.setFieldValue('delegatedHcpIDs', currentIds.filter(id => id !== hcpId));
        
    };

    const renderHcpTable = (
        hcps: IAvailableHcpModel[], 
        searchValue: string, 
        onSearchChange: (value: string) => void,
        totalUnfilteredCount: number,
        currentPage: number,
        onPageChange: (page: number) => void
    ) => {
        // Apply pagination to the filtered data
        const { paginatedData, totalPages } = paginateData(hcps, currentPage, ITEMS_PER_PAGE);

        return (
            <Card withBorder p="sm" mb="md">
                {totalUnfilteredCount > 0 && hcps.length !== totalUnfilteredCount && (
                    <Text size="sm" c="dimmed" mb="sm">
                        {hcps.length} / {totalUnfilteredCount} {t('delegation.form.hcpsShown', 'HCPs shown')}
                    </Text>
                )}
                <TextInput
                    placeholder={t('global.search')}
                    value={searchValue}
                    onChange={(event) => {
                        const newValue = event.currentTarget.value;
                        onSearchChange(newValue);
                        // Reset to first page when searching
                        if (currentPage > 1) 
                            onPageChange(1);
                        
                    }}
                    rightSection={<IconSearch size={16} />}
                    mb="sm"
                />
                {!hcps.length ? (
                    <Text ta="center" c="dimmed" py="md" size="sm">
                        {searchValue ? t('delegation.form.noHcpsMatchingSearch') : t('delegation.form.noHcpsInCategory')}
                    </Text>
                ) : (
                    <>
                        <Table striped>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th style={{width: 50}}></Table.Th>
                                    <Table.Th>{t('delegation.form.hcpName')}</Table.Th>
                                    <Table.Th>{t('delegation.form.specialty')}</Table.Th>
                                    <Table.Th>{t('delegation.form.email')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {paginatedData.map((hcp) => (
                                    <Table.Tr key={hcp.id}>
                                        <Table.Td>
                                            <Checkbox
                                                checked={selectedHcpIds.includes(hcp.id.toString())}
                                                onChange={(event) => 
                                                    handleHcpSelection(hcp.id.toString(), event.currentTarget.checked)
                                                }
                                            />
                                        </Table.Td>
                                        <Table.Td>{hcp.name}</Table.Td>
                                        <Table.Td>{hcp.healthcareProfessionalTypes.find((type: IListItem) => type.id === hcp.specialty)?.name || 'Unknown Specialty'}</Table.Td>
                                        <Table.Td>{hcp.email}</Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                        {totalPages > 1 && (
                            <Group justify="center" mt="md">
                                <Pagination
                                    value={currentPage > totalPages ? 1 : currentPage}
                                    onChange={onPageChange}
                                    total={totalPages}
                                    size="sm"
                                />
                            </Group>
                        )}
                    </>
                )}
            </Card>
        );
    };
    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
                <Text size="sm" c="dimmed">
                    {t('delegation.create.description')}
                </Text>

                <Stack gap="md">
                    <Text fw={500}>{t('delegation.form.selectHcps')}</Text>
                    {alreadyDelegatedHcpIds.size > 0 && (
                        <Text size="sm" c="dimmed">
                            {t('delegation.form.alreadyDelegatedNote', { count: alreadyDelegatedHcpIds.size })}
                        </Text>
                    )}
                    {loadingHcps ? (
                        <Text>{t('delegation.form.loadingHcps')}</Text>
                    ) : sameFacilityHcpsUnfiltered.length === 0 && otherFacilityHcpsUnfiltered.length === 0 ? (
                        <Text ta="center" c="dimmed" py="xl">
                            {t('delegation.form.noAvailableHcps')}
                        </Text>
                    ) : (
                        <Tabs defaultValue="sameFacility" keepMounted={false}>
                            <Tabs.List>
                                <Tabs.Tab 
                                    value="sameFacility" 
                                    leftSection={<IconBuildingHospital size={14} />}
                                >
                                    {t('delegation.form.sameFacilityHcps')} ({sameFacilityHcpsUnfiltered.length})
                                </Tabs.Tab>
                                <Tabs.Tab 
                                    value="otherFacility" 
                                    leftSection={<IconBuilding size={14} />}
                                >
                                    {t('delegation.form.otherHcps')} ({otherFacilityHcpsUnfiltered.length})
                                </Tabs.Tab>
                            </Tabs.List>

                            <Tabs.Panel value="sameFacility" pt="md">
                                {renderHcpTable(
                                    sameFacilityHcps, 
                                    sameFacilitySearch, 
                                    setSameFacilitySearch,
                                    sameFacilityHcpsUnfiltered.length,
                                    sameFacilityPage,
                                    setSameFacilityPage
                                )}
                            </Tabs.Panel>

                            <Tabs.Panel value="otherFacility" pt="md">
                                {renderHcpTable(
                                    otherFacilityHcps, 
                                    otherFacilitySearch, 
                                    setOtherFacilitySearch,
                                    otherFacilityHcpsUnfiltered.length,
                                    otherFacilityPage,
                                    setOtherFacilityPage
                                )}
                            </Tabs.Panel>
                        </Tabs>
                    )}
                    {form.errors.delegatedHcpIDs && (
                        <Text c="red" size="sm">{form.errors.delegatedHcpIDs}</Text>
                    )}
                </Stack>

                <Group justify="flex-end">
                    <Button
                        type="submit"
                        loading={isSubmitting || createDelegation.isPending}
                        disabled={!form.isValid()  || !selectedHcpIds.length}
                        leftSection={<IconUsers size={16} />}
                    >
                        {t('delegation.form.createDelegation')}
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};

export default HcpDelegationForm;
